import React from "react";
import { motion } from "framer-motion";
import { Section, Container, Eyebrow, RevealHeading, Reveal } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { WHY_GOVX, INTEGRITY, IMAGES } from "@/data/site";

const APPROACH = ["Think clearly.", "Build practically.", "Execute relentlessly.", "Measure honestly."];

export default function About() {
  return (
    <>
      <Seo
        title="About | GovX Nigeria"
        description="GovX Nigeria is a strategy, implementation and technology company focused on government, political organisations and public impact — where policy meets reality."
      />
      <PageHero
        eyebrow="About GovX"
        title={["We Work Where", "Policy Meets Reality."]}
        copy="GovX Nigeria is a strategy, implementation and technology company focused on government, political organisations and public impact. We help institutions move from ambition to execution."
        cta="Start a Conversation"
        image={IMAGES.manSuit}
        kicker="Strategy · Delivery · Technology"
      />

      {/* Belief */}
      <Section className="py-24 lg:py-32">
        <Container className="grid gap-12 lg:grid-cols-[0.4fr_1fr]">
          <Eyebrow>Our Belief</Eyebrow>
          <div>
            <RevealHeading
              lines={["Governance is not only", "about making decisions."]}
              as="h2"
              className="font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl"
            />
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-govx-charcoal/70">
                It is about building the systems, capabilities and accountability required to turn
                decisions into outcomes. We bring together strategy, governance, programme
                management, communications, technology, research and implementation.
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Approach */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <Eyebrow dark>Our Approach</Eyebrow>
          <div className="mt-12 grid gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-2 lg:grid-cols-4">
            {APPROACH.map((a, i) => (
              <motion.div
                key={a}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-govx-black p-8 lg:p-10"
              >
                <div className="font-mono text-xs text-govx-lime">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-6 font-display text-2xl font-medium tracking-tight text-govx-paper">{a}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Why GovX */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>Why GovX</Eyebrow>
          <RevealHeading
            lines={["Strategy Without", "Execution Is A Document."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {WHY_GOVX.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.08}>
                <div className="border-t border-govx-charcoal/15 pt-6">
                  <div className="font-mono text-5xl font-light text-govx-green">{w.n}</div>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-govx-charcoal/65">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* Integrity */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Eyebrow dark>Trust &amp; Integrity</Eyebrow>
              <RevealHeading
                lines={["Public Impact Requires", "Public Trust."]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-5xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-7 max-w-lg text-base leading-relaxed text-govx-grey sm:text-lg">
                  GovX maintains clear standards around confidentiality, data responsibility,
                  conflicts of interest, political neutrality within government engagements and
                  responsible use of technology.
                </p>
              </Reveal>
            </div>
            <div className="grid gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-2">
              {INTEGRITY.map((it, i) => (
                <motion.div
                  key={it}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="flex items-center gap-3 bg-govx-black px-6 py-6"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-govx-green" />
                  <span className="font-display text-sm font-medium text-govx-paper">{it}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <CTASection
        title={["Government. Political.", "Digital. Impact."]}
        copy="From ambition to execution. From execution to impact. We don't just advise — we help deliver."
      />
    </>
  );
}
